import { useTranslation } from 'react-i18next'
import { EstadoOt, OrigenOt, SubestadoOt, Semaforo } from './Etiquetas'
import { diasDesde, vencida, tonoDe, tituloOt } from './otLogica'
import { formatFecha } from '../unidades/tipos'

const PRIORIDAD = {
  critica: 'bg-rose-100 text-rose-700', alta: 'bg-orange-100 text-orange-800',
  media: 'bg-sky-100 text-sky-800', baja: 'bg-gray-100 text-gray-600',
}

// The same OT in two short, wide lines (the "Compacta" density): code and title with the days in the stage,
// then project, quantity, group, priority, needed-by date and the small marks. The full detail is one click away.
function TarjetaCompacta({ o, hoy, onClick }) {
  const { t } = useTranslation('operations')
  const dias = diasDesde(o.estado_desde)
  const atrasada = vencida(o, hoy)
  const donde = o.proyecto ? (o.proyecto.code || o.proyecto.name) : o.parte ? (o.responsable?.nombre || t('ot.parte.sinResponsable')) : t(`ot.origenes.${o.origen}`)
  const titulo = tituloOt(o) || t('ot.sinArticulo')
  const detalle = [titulo, o.articulo_codigo, o.solicitante?.display_name && `${t('ot.campos.solicitante')}: ${o.solicitante.display_name}`].filter(Boolean).join(' · ')
  return (
    <button type="button" onClick={onClick} title={detalle}
      className={`w-full text-left rounded-lg border border-gray-200 border-l-4 px-2.5 py-1.5 text-xs shadow-sm transition-all hover:shadow-md ${tonoDe(o.semaforo)}`}>
      <div className="flex items-center gap-1.5 min-w-0">
        <Semaforo semaforo={o.semaforo} />
        <span className="font-mono font-semibold text-gray-900 shrink-0">{o.codigo}</span>
        {o.lote && <span className="px-1 rounded bg-indigo-50 text-indigo-700 text-[10px] font-semibold shrink-0">{o.lote.codigo}</span>}
        {o.parte?.implica_fs && <span className="px-1 rounded bg-red-100 text-red-800 font-semibold text-[10px] shrink-0">F/S</span>}
        <span className="truncate font-medium text-gray-900 flex-1 min-w-0">{titulo}</span>
        <span className="shrink-0 text-gray-500" title={t('ot.campos.enEstado')}>{t('ot.dias', { count: dias })}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-0.5 min-w-0 text-[11px] text-gray-500">
        <span className="truncate min-w-0">{donde}{o.cantidad != null && <> · × {o.cantidad}</>}</span>
        {(o.grupo || o.grupo_propuesto) && (
          <span className={`px-1.5 rounded text-[10px] font-semibold shrink-0 ${o.grupo ? 'bg-gray-900 text-white' : 'border border-dashed border-gray-300 text-gray-500'}`}>
            {(o.grupo || o.grupo_propuesto).nombre}
          </span>
        )}
        {o.prioridad && <span className={`px-1.5 rounded text-[10px] font-medium shrink-0 ${PRIORIDAD[o.prioridad]}`}>{t(`ot.prioridades.${o.prioridad}`)}</span>}
        {o.fecha_requerida && <span className={`shrink-0 ${atrasada ? 'text-rose-600 font-medium' : 'text-gray-400'}`} title={t('ot.requeridaPara', { fecha: formatFecha(o.fecha_requerida) })}>{formatFecha(o.fecha_requerida)}</span>}
        {o.subestado && <span className="shrink-0"><SubestadoOt subestado={o.subestado} /></span>}
        {o.n_adjuntos > 0 && <span className="shrink-0 ml-auto">📎{o.n_adjuntos}</span>}
        {o.n_mensajes > 0 && <span className={`shrink-0 ${o.n_adjuntos > 0 ? '' : 'ml-auto'}`}>💬{o.n_mensajes}</span>}
      </div>
    </button>
  )
}

// One OT on the board: number, item, project or origin, quantity, requester,
// days in its state and the needed-by date (red once past).
export default function TarjetaOt({ o, hoy, onClick, compacta = false }) {
  const { t } = useTranslation('operations')
  const dias = diasDesde(o.estado_desde)
  const atrasada = vencida(o, hoy)
  if (compacta) return <TarjetaCompacta o={o} hoy={hoy} onClick={onClick} />      // (after the hooks, so the density can change)
  return (
    <button type="button" onClick={onClick}
      className={`w-full text-left rounded-xl border border-gray-200 border-l-4 p-3 text-sm shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 ${tonoDe(o.semaforo)}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5">
          <Semaforo semaforo={o.semaforo} />
          <span className="font-mono text-xs font-semibold text-gray-900">{o.codigo}</span>
          {o.lote && <span className="px-1 rounded bg-indigo-50 text-indigo-700 text-[10px] font-semibold" title={t('ot.lote.titulo')}>{o.lote.codigo}</span>}
        </span>
        <div className="flex items-center gap-1">
          <OrigenOt origen={o.origen} />
          {o.estado === 'propuesta' && <EstadoOt estado={o.estado} />}
        </div>
      </div>
      <p className="mt-1 font-medium text-gray-900 line-clamp-2">
        {tituloOt(o) || t('ot.sinArticulo')}
      </p>
      {o.categoria && <p className="text-[11px] text-violet-700 mt-0.5">{o.categoria.nombre}</p>}
      {o.parte && (
        <p className="text-[11px] text-gray-600 mt-0.5 flex items-center gap-1.5">
          <span>{t('ot.parte.corto', { numero: o.parte.numero })} · <span className="font-medium">{o.parte.unidad_codigo}</span></span>
          {o.parte.implica_fs && <span className="px-1 rounded bg-red-100 text-red-800 font-semibold text-[10px]">F/S</span>}
        </p>
      )}
      {o.articulo_codigo && o.articulo_descripcion && <p className="text-[11px] font-mono text-gray-500">{o.articulo_codigo}</p>}
      <p className="text-xs text-gray-500 mt-1 truncate">
        {o.proyecto ? (o.proyecto.code || o.proyecto.name) : o.parte ? (o.responsable?.nombre || t('ot.parte.sinResponsable')) : t(`ot.origenes.${o.origen}`)}
        {o.cantidad != null && <> · × {o.cantidad}</>}
      </p>
      <div className="flex items-center justify-between gap-2 mt-2 text-[11px] text-gray-500">
        <span className="truncate">{o.solicitante?.display_name}</span>
        <span className="shrink-0" title={t('ot.campos.enEstado')}>{t('ot.dias', { count: dias })}</span>
      </div>
      {o.fecha_requerida && (
        <p className={`text-[11px] mt-0.5 ${atrasada ? 'text-rose-600 font-medium' : 'text-gray-400'}`}>
          {t('ot.requeridaPara', { fecha: formatFecha(o.fecha_requerida) })}
        </p>
      )}
      {o.subestado && <div className="mt-1.5"><SubestadoOt subestado={o.subestado} /></div>}
      {(o.grupo || o.grupo_propuesto || o.prioridad) && (
        <div className="flex flex-wrap items-center gap-1 mt-1.5">
          {(o.grupo || o.grupo_propuesto) && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${o.grupo ? 'bg-gray-900 text-white' : 'border border-dashed border-gray-300 text-gray-500'}`}
              title={o.grupo ? t('ot.campos.grupo') : t('ot.campos.grupo_propuesto')}>
              {(o.grupo || o.grupo_propuesto).nombre}
            </span>
          )}
          {o.prioridad && <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${PRIORIDAD[o.prioridad]}`}>{t(`ot.prioridades.${o.prioridad}`)}</span>}
        </div>
      )}
      {o.folio_of && <p className="text-[11px] font-mono text-indigo-700 mt-1">OF {o.folio_of}</p>}
      {(o.n_adjuntos > 0 || o.n_mensajes > 0) && (
        <p className="text-[11px] text-gray-400 mt-0.5 flex gap-2">
          {o.n_adjuntos > 0 && <span>📎 {o.n_adjuntos}</span>}
          {o.n_mensajes > 0 && <span>💬 {o.n_mensajes}</span>}
        </p>
      )}
    </button>
  )
}
